"use client";

import { useEffect, useState } from "react";

import { useOptionalCurrency } from "@/features/currency/_hooks/useCurrency";
import { fetchFxRatesAction } from "@/features/currency/_logic/actions";
import type { FxRateMap } from "@/features/currency/_logic/fx-service";

/**
 * USD-based FX rates for the estimator: the dashboard's loaded rates, fetched once when the
 * dashboard skipped them (USD-only mode) and the estimate is in another currency.
 * @param currency - The estimate's display currency.
 * @returns Rate map (USD value of one unit of each currency); empty until loaded.
 * @throws Never.
 */
export function useFxRates(currency: string): FxRateMap {
  // Hooks
  const currencyContext = useOptionalCurrency();

  // States
  const [fetched, setFetched] = useState<FxRateMap | null>(null);

  // Derived
  const contextRates = currencyContext?.rates ?? {};
  const hasContextRates = Object.keys(contextRates).length > 0;
  const needsFetch = !hasContextRates && currency !== "USD" && fetched === null;

  // Effects
  useEffect(() => {
    if (!needsFetch) return;
    void fetchFxRatesAction()
      .then(setFetched)
      .catch(() => setFetched({}));
  }, [needsFetch]);

  return hasContextRates ? contextRates : (fetched ?? {});
}
